import { useCallback, useEffect, useMemo, useState } from "react";

import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, Route, Routes, useNavigate, useParams } from "react-router";

import {
  CheckIcon,
  CircleCheckIcon,
  ExternalLinkIcon,
  GhostIcon,
  LightbulbIcon,
  SearchIcon,
  SendIcon,
  PencilIcon,
  StarIcon,
  TrophyIcon,
  UploadIcon,
  UserIcon,
} from "lucide-react";
import { useMessagesStore } from "stores/messages";

import { socialMediaLinks } from "simplydo/core/users";
import type { OpenAPI } from "simplydo/interfaces";
import type { MessageThreadWithParticipants } from "simplydo/interfaces/types/messageTypes";
import type { UserProfilePrepared } from "simplydo/interfaces/types/userTypes";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import type { UiColor } from "simplydo/react/theme";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { BRAND_ICONS } from "simplydo/react/ui/brand-icon";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { Container } from "simplydo/react/ui/container";
import { Divider } from "simplydo/react/ui/divider";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H1, H2, H3, H5, Subheader } from "simplydo/react/ui/header";
import { Icon, IconData } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { TagView } from "components/lib/Choosers/Tags/TagModalChooser";
import PageLoadError from "components/lib/PageLoadError";
import ReportContentChip from "components/lib/ReportContent/ReportContentChip";
import { getRoleIcon } from "components/lib/Roles/roleIcons";
import { SideNav } from "components/lib/SideNav";
import type { SideNavItemProps } from "components/lib/SideNav/SideNavComponent";
import { Banner } from "components/lib/UI";
import UserStatusBar from "components/lib/UserStatusBar";
import AchievementAwarder from "components/lib/Users/AchievementAwarder";

import { ProfileDetails } from "./Details/Page";
import ProfileFollowing from "./Following";
import ProfileIdeas from "./ProfileIdeas";
import type {
  ProfileUpload,
  PublicUser,
  SocialMediaKey,
  UserIdeaOptions,
} from "./types";
const UserProfileLink = styled.a`
  display: inline;
  text-overflow: ellipsis;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
`;

const LargeAvatar = styled.div`
  display: inline-block;
  background-size: cover;
  background-position: center center;
  background-color: white;
  border-radius: 50%;
  box-shadow: 0px 3px 10px 20px rgba(0, 0, 0, 0.05);
  border: ${({ theme }) => (theme.sizes.isMobile ? 2 : 5)}px solid white;
  height: ${({ theme }) => (theme.sizes.isMobile ? 50 : 150)}px;
  width: ${({ theme }) => (theme.sizes.isMobile ? 50 : 150)}px;
`;

const Profile = () => {
  const user = useCurrentUser();

  const onMessageThreadSelected = useMessagesStore(
    (state) => state.selectThread,
  );
  const { t } = useTranslation();

  const [profileUser, setProfileUser] = useState<PublicUser | null>(null);
  const [ideas, setIdeas] = useState<OpenAPI.Schemas["Idea"][]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [ideaOptions, setIdeaOptions] = useState<UserIdeaOptions>({
    filter: "",
    showDraft: true,
    showSubmitted: true,
    sort: "newest",
  });
  const [achievementAwardModalOpen, setAchievementAwardOpen] = useState(false);
  const { filter, showDraft, showSubmitted, sort } = ideaOptions;

  const { id: matchId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const getUser = useCallback((id: string) => {
    setLoading(true);
    setErrorMessage("");
    api.users.get(
      id,
      (u) => {
        setProfileUser(u);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        setErrorMessage(err.message);
      },
    );
  }, []);

  const refreshAchievements = useCallback(() => {
    if (!matchId) return;
    api.users.get(
      matchId,
      (data) => {
        setProfileUser((prevUser) =>
          prevUser
            ? { ...prevUser, customAchievements: data.customAchievements }
            : null,
        );
      },
      (err) => setErrorMessage(err.message),
    );
  }, [matchId]);

  const fetchIdeas = useCallback((id: string) => {
    setIdeasLoading(true);
    api.users.ideas(
      id,
      null,
      ({ ideas: userIdeas }) => {
        setIdeasLoading(false);
        setIdeas(userIdeas);
      },
      () => setIdeasLoading(false),
    );
  }, []);

  useEffect(() => {
    if (!matchId) return;
    getUser(matchId);
    fetchIdeas(matchId);
  }, [matchId, fetchIdeas, getUser]);

  const setFilters = useCallback((filters: Partial<UserIdeaOptions>) => {
    setIdeaOptions((prev) => ({ ...prev, ...filters }));
  }, []);

  const messageUser = useCallback(() => {
    if (!matchId || !profileUser) return;
    api.messages.findThreadByUser(
      matchId,
      (data) => {
        const newThread = {
          _id: `newThread:${matchId}`,
          createdAt: new Date().toISOString(),
          participants: [profileUser._id],
          participantUsers: [profileUser],
        } as MessageThreadWithParticipants;
        onMessageThreadSelected(
          (data?.thread as MessageThreadWithParticipants | undefined) ??
            newThread,
        );
      },
      (err) => toast.error(err.message),
    );
  }, [profileUser, onMessageThreadSelected, matchId]);

  const checkURL = (url: string) =>
    url.indexOf("http") === 0 ? url : `http://${url}`;

  const ghost = useCallback(() => {
    if (!matchId) return;
    util
      .confirm(
        "Really ghost as this user?",
        "Ghosting allows you to view the platform and carry out actions as though you are the user. You can un-ghost at any time by tapping the avatar at the top-right of the screen.",
      )
      .then(() =>
        api.users.ghost(
          matchId,
          () => {
            api.invalidateQueries();
            navigate("/");
          },
          (err) => toast.error(err.message),
        ),
      )
      .catch(() => {});
  }, [navigate, matchId]);

  const ProfileIdeaFilters = useMemo(
    () => (
      <div style={{ padding: 0, marginTop: 20 }}>
        <FormField>
          <FormLabel>{t("filter.title")}</FormLabel>
          <Input
            fluid
            placeholder={t("filter.search")}
            icon={SearchIcon}
            value={filter}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFilters({ filter: e.target.value })
            }
          />
        </FormField>
        <Divider hidden />
        <FormField>
          <Checkbox
            checked={showDraft}
            onChange={() => setFilters({ showDraft: !showDraft })}
          >
            {t("ideas.yours.sidebar.draft")}
          </Checkbox>
          <Checkbox
            checked={showSubmitted}
            onChange={() => setFilters({ showSubmitted: !showSubmitted })}
          >
            {t("ideas.yours.sidebar.submitted")}
          </Checkbox>
        </FormField>
        <Divider hidden />
        <FormField>
          <Checkbox
            type="radio"
            name="sort"
            value="newest"
            checked={sort === "newest"}
            onChange={() => setFilters({ sort: "newest" })}
          >
            {t("order.newest")}
          </Checkbox>
          <Checkbox
            type="radio"
            name="sort"
            value="oldest"
            checked={sort === "oldest"}
            onChange={() => setFilters({ sort: "oldest" })}
          >
            {t("order.oldest")}
          </Checkbox>
        </FormField>
      </div>
    ),
    [filter, setFilters, showDraft, showSubmitted, sort, t],
  );

  if (errorMessage)
    return (
      <PageLoadError
        title={t("users.profile.problem")}
        message={errorMessage}
      />
    );

  if (!profileUser) {
    return null;
  }

  const {
    profile = {} as UserProfilePrepared,
    ownerOrganisation,
    organisation,
    ownerTags,
    canBeMessagedByCurrentUser,
  } = profileUser;
  const {
    fullName,
    tagline,
    website,
    department,
    jobTitle,
    avatar,
    uploads = [],
  } = profile;
  const isYourProfile = profileUser._id === user?._id;

  const profileSocialElements = (
    [
      "facebook",
      "twitter",
      "linkedin",
      "instagram",
      "github",
    ] as SocialMediaKey[]
  ).filter((key) => !!profile[key]);

  const mobileButtonSize = theme.sizes.isMobile ? "small" : undefined;
  const samlMappings =
    user?.ownerOrganisation?.auth?.saml?.customMappings ?? [];

  return (
    <div>
      <Helmet title={fullName} />
      {user?.ownerOrganisation?.achievementsEnabled !== false &&
      util.hasPermission(
        user,
        "org.viewDashboard",
        user?.ownerOrganisation?._id,
      ) ? (
        <AchievementAwarder
          targetUser={achievementAwardModalOpen ? profileUser : null}
          user={user}
          onClose={() => {
            setAchievementAwardOpen(false);
            refreshAchievements();
          }}
        />
      ) : null}
      <UserStatusBar profileUser={profileUser} />
      <Banner
        marginless
        image={util.getThumbnail(profile, "coverImage", "2560x400")}
        style={{ backgroundColor: "rgb(200,200,200)" }}
      >
        <Container>
          <div className="overlay" />
          <div className="overlay-body">
            <Grid mobile={1} columns={16}>
              <GridColumn span={16} computer={2}>
                {!theme.sizes.isMobile && (
                  <>
                    <LargeAvatar
                      style={{
                        backgroundImage: `url(${util.mixinCssUrlFallback(util.avatarUrl(profileUser, "300x300"), util.avatarUrl())})`,
                        marginLeft: -10,
                      }}
                    />
                    {isYourProfile && !avatar && (
                      <div style={{ marginTop: 10 }}>
                        <Link
                          style={{ color: "#e2e7f3" }}
                          to={"/preferences/profile"}
                        >
                          <Icon icon={UploadIcon} /> Upload a profile image
                        </Link>
                      </div>
                    )}
                  </>
                )}
              </GridColumn>
              <GridColumn span={16} computer={7}>
                {theme.sizes.isMobile ? (
                  <div style={{ marginBottom: -40 }}>
                    <div style={{ display: "flex" }}>
                      <div
                        style={{ position: "relative", display: "inline-flex" }}
                      >
                        <LargeAvatar
                          style={{
                            backgroundImage: `url(${util.mixinCssUrlFallback(util.avatarUrl(profileUser, "300x300"), util.avatarUrl())})`,
                          }}
                        />
                        {profileUser?.isAdmin && (
                          <Popup
                            trigger={
                              <div
                                style={{
                                  position: "absolute",
                                  top: 0.5,
                                  left: 31,
                                }}
                              >
                                <Icon
                                  icon={CircleCheckIcon}
                                  size="large"
                                  style={{
                                    color: "#45abff",
                                    position: "absolute",
                                  }}
                                />
                                <Icon
                                  icon={CheckIcon}
                                  style={{
                                    position: "relative",
                                    color: "white",
                                    left: 4.2,
                                    top: 2,
                                  }}
                                />
                              </div>
                            }
                          >
                            Admin user
                          </Popup>
                        )}
                      </div>
                      <div style={{ marginLeft: 10 }}>
                        <H2>{fullName}</H2>
                        {ownerOrganisation && (
                          <H5 style={{ marginTop: -12 }}>
                            Member of {ownerOrganisation?.name}
                          </H5>
                        )}
                        {isYourProfile && !avatar && (
                          <div style={{ marginTop: 10 }}>
                            <Link
                              style={{ color: "#e2e7f3" }}
                              to={"/preferences/profile"}
                            >
                              <Icon icon={UploadIcon} /> Upload a profile image
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                    {(department || jobTitle) && (
                      <H5>
                        {jobTitle} {department && <span>({department})</span>}
                      </H5>
                    )}
                    {website && (
                      <div>
                        <UserProfileLink
                          href={checkURL(website)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Icon icon={ExternalLinkIcon} /> {website}
                        </UserProfileLink>
                      </div>
                    )}
                    {tagline && (
                      <div
                        style={{
                          marginBottom: 15,
                          color: "white",
                          fontStyle: "italic",
                        }}
                      >
                        <span>{tagline}</span>
                      </div>
                    )}
                    {ownerTags && ownerTags.length > 0 ? (
                      <TagView tags={ownerTags} />
                    ) : null}
                  </div>
                ) : (
                  <div style={{ marginLeft: 20 }}>
                    {ownerOrganisation && (
                      <H5 style={{ margin: 0 }}>
                        Member of {ownerOrganisation?.name}
                      </H5>
                    )}
                    <div
                      style={{ position: "relative", display: "inline-flex" }}
                    >
                      <H1>{fullName}</H1>
                      {profileUser.iconRoles?.map((role, index) => (
                        <Popup
                          key={`${role.name}-${index}`}
                          trigger={
                            <Icon
                              icon={getRoleIcon(role?.icon?.name)}
                              size="large"
                              color={role?.icon?.colour as UiColor}
                              style={{ marginLeft: 5 }}
                            />
                          }
                        >
                          {role?.name}
                        </Popup>
                      ))}
                    </div>
                    {(department || jobTitle) && (
                      <H3>
                        {jobTitle}{" "}
                        {department && <Subheader>({department})</Subheader>}
                      </H3>
                    )}
                    {tagline && (
                      <div
                        style={{
                          color: "white",
                          fontStyle: "italic",
                          marginBottom: 8,
                        }}
                      >
                        {tagline}
                      </div>
                    )}
                    {website && (
                      <div
                        style={{
                          background: "white",
                          padding: "2px 5px",
                          borderRadius: 3,
                          maxWidth: "fit-content",
                        }}
                      >
                        <UserProfileLink
                          href={checkURL(website)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Icon icon={ExternalLinkIcon} /> {website}
                        </UserProfileLink>
                      </div>
                    )}
                  </div>
                )}
              </GridColumn>
              <GridColumn
                span={16}
                computer={7}
                style={{ textAlign: "right", alignSelf: "end" }}
              >
                <div style={{ marginTop: 30 }}>
                  {!isYourProfile &&
                  util.hasPermission(
                    user,
                    "super.ghostUser",
                    profileUser.ownerOrganisation?._id,
                  ) ? (
                    <Button
                      size={mobileButtonSize}
                      onClick={ghost}
                      variant="outline"
                    >
                      <Icon icon={GhostIcon} />
                      Ghost user
                    </Button>
                  ) : null}
                  {user?.ownerOrganisation?.achievementsEnabled !== false &&
                  (profileUser.customAchievements?.length ?? 0) > 0 &&
                  (util.hasPermission(user, "org.viewDashboard") ||
                    util.hasPermission(user, "super.viewDashboard")) ? (
                    <Button
                      size={mobileButtonSize}
                      onClick={() => setAchievementAwardOpen(true)}
                      variant="outline"
                    >
                      <Icon icon={TrophyIcon} />
                      Award custom achievement
                    </Button>
                  ) : null}
                  {!isYourProfile &&
                  canBeMessagedByCurrentUser &&
                  user?.ownerOrganisation?.enabledFeatures?.includes(
                    "directMessaging",
                  ) ? (
                    <Button
                      size={mobileButtonSize}
                      style={{ marginTop: 5 }}
                      onClick={messageUser}
                      variant="outline"
                    >
                      <Icon icon={SendIcon} />
                      Send a message
                    </Button>
                  ) : null}
                  {isYourProfile ? (
                    <ButtonLink
                      size={mobileButtonSize}
                      style={{ marginTop: 5 }}
                      variant="outline"
                      to={"/preferences/profile"}
                      color="onSecondary"
                    >
                      <Icon icon={PencilIcon} />
                      {t("users.preferences.profile.edit")}
                    </ButtonLink>
                  ) : null}
                </div>
              </GridColumn>
            </Grid>
          </div>
        </Container>
      </Banner>

      <SideNav
        navItems={
          [
            {
              key: "profile",
              content: t("generic.profile"),
              iconLeft: UserIcon,
              to: "..",
              active: window.location.pathname === `/users/${profileUser._id}`,
            },
            util.hasPermission(user, "org.viewDashboard", organisation) ||
            profileUser._id === user?._id
              ? {
                  key: "ideas",
                  content: t("generic.ideas"),
                  iconLeft: LightbulbIcon,
                  to: "../ideas",
                  active:
                    window.location.pathname ===
                    `/users/${profileUser._id}/ideas`,
                }
              : null,
            util.hasPermission(user, "org.viewDashboard", organisation) ||
            profileUser._id === user?._id
              ? {
                  key: "following",
                  content: "Following",
                  iconLeft: StarIcon,
                  to: "../following",
                  active:
                    window.location.pathname ===
                    `/users/${profileUser._id}/following`,
                }
              : null,

            ...(window.location.pathname === `/users/${profileUser._id}`
              ? [
                  profileSocialElements.length
                    ? {
                        key: "links",
                        type: "custom",
                        content: (
                          <H5 style={{ textTransform: "uppercase" }}>
                            {t("generic.links")}
                          </H5>
                        ),
                      }
                    : null,

                  ...profileSocialElements.map((socialElement) => {
                    const linkConfig = socialMediaLinks[socialElement];
                    const BrandIcon =
                      "brand" in linkConfig
                        ? BRAND_ICONS[linkConfig.brand]
                        : undefined;
                    return {
                      key: socialElement,
                      type: "link" as const,
                      iconLeft: BrandIcon ? (
                        <Icon
                          icon={BrandIcon}
                          style={{ color: linkConfig.color }}
                        />
                      ) : "icon" in linkConfig ? (
                        linkConfig.icon
                      ) : undefined,
                      iconLeftAlt:
                        "iconLeftAlt" in linkConfig
                          ? linkConfig.iconLeftAlt
                          : undefined,
                      content: linkConfig.content,
                      to: linkConfig.link + (profile[socialElement] ?? ""),
                      external: true,
                    };
                  }),

                  (uploads as ProfileUpload[]).length
                    ? {
                        key: "uploads",
                        type: "custom",
                        content: (
                          <H5 style={{ textTransform: "uppercase" }}>
                            Uploads
                          </H5>
                        ),
                      }
                    : null,

                  ...(uploads as ProfileUpload[]).map((upload) => ({
                    key: upload.storedName,
                    type: "link" as const,
                    iconLeft: util.getFileIcon(upload.storedName) as IconData,
                    external: true,
                    content: upload.title,
                    to: upload.url ?? "",
                  })),
                ].filter((item) => Boolean(item))
              : []),

            window.location.pathname === `/users/${profileUser._id}/ideas`
              ? {
                  key: "ideaFilters",
                  type: "custom",
                  content: theme.sizes.isMobile ? (
                    <Accordion>
                      <AccordionTitle index={0}>
                        {`${t("generic.idea")} filters`}
                      </AccordionTitle>
                      <AccordionContent index={0}>
                        {ProfileIdeaFilters}
                      </AccordionContent>
                    </Accordion>
                  ) : (
                    ProfileIdeaFilters
                  ),
                }
              : null,
          ].filter((item) => Boolean(item)) as SideNavItemProps[]
        }
        main={
          <Routes>
            <Route
              path="/"
              element={
                <ProfileDetails
                  loading={loading}
                  profileUser={profileUser}
                  isYourProfile={isYourProfile}
                />
              }
            />
            {(util.hasPermission(user, "org.viewDashboard", organisation) ||
              profileUser._id === user?._id) && (
              <Route
                path="ideas"
                element={
                  <ProfileIdeas
                    ideas={ideas}
                    loading={ideasLoading}
                    sort={sort}
                    filter={filter}
                    showDraft={showDraft}
                    showSubmitted={showSubmitted}
                    profileUser={profileUser}
                  />
                }
              />
            )}
            {(util.hasPermission(user, "org.viewDashboard", organisation) ||
              profileUser._id === user?._id) && (
              <Route
                path="following"
                element={
                  <ProfileFollowing
                    loading={loading}
                    profileUser={profileUser}
                  />
                }
              />
            )}
          </Routes>
        }
        afterContent={
          <>
            <Divider />
            {samlMappings.length > 0 &&
            profileUser.profile?.attributes &&
            Object.keys(profileUser.profile.attributes).length > 0 ? (
              <div style={{ marginBottom: 10 }}>
                {samlMappings.map(
                  (mapping: { localKey: string; displayName: string }) =>
                    profileUser.profile?.attributes?.[mapping.localKey] && (
                      <div key={mapping.localKey} style={{ marginBottom: 5 }}>
                        {mapping.displayName}:{" "}
                        {profileUser.profile?.attributes?.[mapping.localKey]}
                      </div>
                    ),
                )}
              </div>
            ) : null}
            <ReportContentChip forType="user" forId={profileUser._id} />
          </>
        }
      />
    </div>
  );
};

export default Profile;
